/**
 * Service Complexity Widget (issue #1657, part of epic #1650)
 *
 * Ranked list of the caller's in-scope services by composite complexity
 * score, matching `homepage-dashboard-spec.md`'s "Service Complexity Score"
 * widget. The scoring heuristic lives in `backend/src/widgets/
 * serviceComplexity.ts` — see that module's doc. Follows the single-query +
 * `useStatisticsWidget` pattern shared by `DependencyHealthWidget`.
 */

import { useServiceComplexity, type ServiceComplexityEntry } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './ServiceComplexityWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'service-complexity',
  title: 'Service Complexity',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface ServiceComplexityStats {
  services: ServiceComplexityEntry[];
}

type ServiceComplexityQueryData = ReturnType<typeof useServiceComplexity>['data'];

function extractServiceComplexityStats(
  data: ServiceComplexityQueryData
): ServiceComplexityStats | null {
  if (!data?.serviceComplexity) {
    return null;
  }
  return data.serviceComplexity;
}

/** Score-tier class suffix, matching `recommendationFor`'s tiers on the backend. */
function scoreTierClass(score: number): string {
  if (score >= 75) return 'high';
  if (score >= 50) return 'moderate';
  return 'low';
}

export function ServiceComplexityWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useServiceComplexity(10),
    extractServiceComplexityStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.services.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="service-complexity-widget__empty">
          <IconElement name="hub" className="service-complexity-widget__empty-icon" />
          <p className="forge-typography--body2">No services available to score</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="service-complexity-widget__list" data-testid="service-complexity-list">
        {stats.services.map((service) => (
          <li key={service.entityId} className="service-complexity-widget__item">
            <div className="service-complexity-widget__item-header">
              <span className="service-complexity-widget__item-name forge-typography--body2">
                {service.name}
              </span>
              <span
                className={`service-complexity-widget__score service-complexity-widget__score--${scoreTierClass(
                  service.complexityScore
                )}`}
              >
                {service.complexityScore}
              </span>
            </div>
            <div className="service-complexity-widget__item-factors forge-typography--caption">
              {service.inboundDeps} in · {service.outboundDeps} out · {service.techCount} tech ·{' '}
              {service.ageMonths}mo old
            </div>
            <div className="service-complexity-widget__item-recommendation forge-typography--caption">
              {service.recommendation}
            </div>
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
